import { ensureValue } from '@rematter/ui-web';

import {
  ServiceAssetQuantity,
  ServiceRecord,
} from '~/modules/transportation/types';
import { createSubscriptionHook } from '~/shared/providers/api';
import {
  maybeParseZonedDateTime,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const useServiceChangesSubscription = createSubscriptionHook(
  'transportation.serviceChanges',
  (_: void) => ({
    transportationServiceChanges: {
      __args: { ids: [] },
      id: true,
      serviceType: {
        id: true,
        name: true,
      },
      status: true,
      documentNumber: true,
      rank: true,
      priority: true,
      scheduledWindowStart: true,
      scheduledWindowEnd: true,
      startedAt: true,
      completedAt: true,
      assignedAt: true,
      estimatedArrival: true,
      internalNotes: true,
      externalNotes: true,
      driverNotes: true,
      driverId: true,
      haulerId: true,
      truckId: true,
      routeId: true,
      trailerId: true,
      facilityGroupId: true,
      facilityId: true,
      accountId: true,
      accountAddressId: true,
      driver: {
        id: true,
        firstName: true,
        lastName: true,
      },
      truck: {
        id: true,
        licensePlate: true,
      },
      trailer: {
        id: true,
        licensePlate: true,
      },
      actions: {
        actionType: true,
        assetId: true,
        assetTypeId: true,
        asset: {
          id: true,
          identifier: true,
        },
        assetType: {
          id: true,
          name: true,
          capacity: true,
        },
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
  {
    select: ({
      transportationServiceChanges: change,
    }): ServiceRecord | undefined =>
      change
        ? {
            id: change.id,
            serviceTypeId: change.serviceType?.id,
            serviceTypeName: change.serviceType?.name,
            status: change.status,
            documentNumber: change.documentNumber,
            rank: change.rank,
            priority: change.priority,
            scheduledWindowStart: parseZonedDateTime(
              change.scheduledWindowStart ?? '',
            ),
            scheduledWindowEnd: parseZonedDateTime(
              change.scheduledWindowEnd ?? '',
            ),
            startedAt: maybeParseZonedDateTime(change.startedAt),
            completedAt: maybeParseZonedDateTime(change.completedAt),
            assignedAt: maybeParseZonedDateTime(change.assignedAt),
            estimatedArrival: maybeParseZonedDateTime(change.estimatedArrival),
            internalNotes: change.internalNotes ?? undefined,
            externalNotes: change.externalNotes ?? undefined,
            driverNotes: change.driverNotes ?? undefined,
            driver: change.driver ?? undefined,
            driverId: change.driverId ?? undefined,
            haulerId: change.haulerId ?? undefined,
            truck: change.truck ?? undefined,
            truckId: change.truckId ?? undefined,
            trailer: change.trailer ?? undefined,
            trailerId: change.trailerId ?? undefined,
            routeId: change.routeId ?? undefined,
            facilityGroupId: change.facilityGroupId ?? undefined,
            facilityId: change.facilityId ?? undefined,
            accountId: change.accountId ?? undefined,
            accountAddressId: change.accountAddressId ?? undefined,
            assetQuantities: (() => {
              const assetTypeMap = new Map<string, ServiceAssetQuantity>();
              (change.actions ?? []).forEach((action) => {
                if (!action.assetType) return;
                const assetTypeId = action.assetType.id;
                if (assetTypeMap.has(assetTypeId)) {
                  ensureValue(assetTypeMap.get(assetTypeId)).quantity += 1;
                } else {
                  assetTypeMap.set(assetTypeId, {
                    assetType: {
                      id: action.assetType.id,
                      name: action.assetType.name,
                      capacity: action.assetType.capacity,
                    },
                    quantity: 1,
                  });
                }
              });
              return Array.from(assetTypeMap.values());
            })(),
            timestamps: {
              createdAt: parseZonedDateTime(change.timestamps.createdAt),
              updatedAt: parseZonedDateTime(change.timestamps.updatedAt),
            },
          }
        : undefined,
  },
);
